// src/components/admin/SubmissionCard.js

import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Divider,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DataDisplay from "./DataDisplay"; // Ensure the path is correct
import { db } from "../../../firebase"; // Adjust path as needed
import { updateDoc, deleteDoc, doc } from "firebase/firestore";

const SubmissionCard = ({ submission, onForcePayment, onDeleteSubmission }) => {
  const {
    paymentData,
    profileData,
    challengeData,
    title,
    id,
    description,
    status,
    fundingGoal,
    videoUploaded,
    submissionSaleId,
  } = submission;

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Close the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Update payment status to COMPLETED if it is INCOMPLETE
  const handleForcePayment = async () => {
    try {
      const submissionRef = doc(db, "submissions", id);
      await updateDoc(submissionRef, {
        "paymentData.status": "COMPLETED",
      });
      onForcePayment(id);
      setSnackbarMessage("Payment status updated to COMPLETED.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating payment status:", error);
      setSnackbarMessage("Failed to update payment status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Delete the submission document after confirmation
  const handleDeleteSubmission = async () => {
    try {
      const submissionRef = doc(db, "submissions", id);
      await deleteDoc(submissionRef);
      onDeleteSubmission(id);
      setSnackbarMessage("Submission deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting submission:", error);
      setSnackbarMessage("Failed to delete submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setOpenDeleteDialog(false);
    setDeleteConfirmText("");
  };

  // Build the thumbnail URL using submissionSaleId
  const thumbnailUrl =
    submissionSaleId &&
    `https://swayer.blob.core.windows.net/${submissionSaleId}-pub/_thumb_large.jpg`;

  return (
    <>
      <Accordion sx={{ mb: 2, borderRadius: "5px" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ mr: 2 }}>
              <img
                src={
                  videoUploaded && submissionSaleId
                    ? thumbnailUrl
                    : "https://dummyimage.com/200x200/cccccc/333333.png?text=No+Media+Available"
                }
                alt="Thumbnail"
                style={{ width: "200px", borderRadius: "5px" }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "normal", fontFamily: "arial, sans-serif" }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    {title || "No Title"}
                  </span>
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "normal", fontFamily: "arial, sans-serif" }}
                >
                  id: {id}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "normal", fontFamily: "arial, sans-serif" }}
                >
                  <span style={{ fontWeight: "bold" }}>Status:</span> {status}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "normal", fontFamily: "arial, sans-serif" }}
                >
                  <span style={{ fontWeight: "bold" }}>Funding:</span> $
                  {fundingGoal}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "normal", fontFamily: "arial, sans-serif" }}
                >
                  <span style={{ fontWeight: "bold" }}>Media Uploaded:</span>{" "}
                  {videoUploaded ? "Yes" : "No"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Description</Typography>
            <Typography variant="body2">{description}</Typography>
          </Box>
          <Divider />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Payment Data</Typography>
            <DataDisplay data={paymentData} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle1">Profile Data</Typography>
            <DataDisplay data={profileData} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle1">Challenge Data</Typography>
            <DataDisplay data={challengeData} />
          </Box>
          {/* Buttons Section */}
          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
            {paymentData && paymentData.status === "INCOMPLETE" && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleForcePayment}
              >
                Force Payment
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete Submission
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this submission? Please type{" "}
            <strong>delete</strong> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Type delete to confirm"
            fullWidth
            variant="outlined"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteSubmission}
            disabled={deleteConfirmText.toLowerCase() !== "delete"}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubmissionCard;
